import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMail, FiMapPin } from 'react-icons/fi';
import useSiteSettings from '../hooks/useSiteSettings';
import './LegalPages.css';

/* ── Fallback hero images (Unsplash, no auth needed) ─────────────────────── */
const HERO_IMAGES = {};

const DEFAULT_CONTACT = {
  phone: '+91 98765 43210',
  email: 'support@angelprinters.com',
  primaryAddress: 'Noida, Uttar Pradesh',
};

/* ════════════════════════════════════════════════════════════════════════════
   SHARED LAYOUT COMPONENTS
   ════════════════════════════════════════════════════════════════════════════ */

/* ── Hero ──────────────────────────────────────────────────────────────────── */
const PolicyHero = ({ eyebrow, titlePlain, titleHighlight, intro }) => (
  <section className="lp-hero">
    <div className="container">
      {/* <span className="lp-hero-eyebrow">{eyebrow}</span> */}
      <h1 className="lp-hero-title">
        {titlePlain}{' '}
        <span className="lp-hero-highlight">{titleHighlight}</span>
      </h1>
      <p className="lp-hero-intro">{intro}</p>
    </div>
  </section>
);

/* ── Section (numbered) ────────────────────────────────────────────────────── */
const Section = ({ number, title, children }) => (
  <div className="lp-section">
    <div className="lp-section-header">
      <span className="lp-section-num">{number}.</span>
      <h2 className="lp-section-title">{title}</h2>
    </div>
    <div className="lp-section-body">{children}</div>
  </div>
);

const P = ({ children }) => (
  <p className="lp-para">{children}</p>
);

const Bullets = ({ items }) => (
  <ul className="lp-bullets">
    {items.map((item, i) => (
      <li key={i} className="lp-bullet-item">
        <span className="lp-bullet-dot" aria-hidden="true" />
        {item}
      </li>
    ))}
  </ul>
);

/* ── Contact row ───────────────────────────────────────────────────────────── */
const ContactRow = ({ contact }) => (
  <div className="lp-contact-row">
    <a href={`tel:${(contact.phone || '').replace(/\s/g, '')}`} className="lp-contact-item">
      <FiPhone className="lp-contact-icon" />
      <span>{contact.phone || DEFAULT_CONTACT.phone}</span>
    </a>
    <a href={`mailto:${contact.email}`} className="lp-contact-item">
      <FiMail className="lp-contact-icon" />
      <span>{contact.email || DEFAULT_CONTACT.email}</span>
    </a>
    <div className="lp-contact-item">
      <FiMapPin className="lp-contact-icon" />
      <span>{contact.primaryAddress || DEFAULT_CONTACT.primaryAddress}</span>
    </div>
  </div>
);

/* ── Last Updated bar ──────────────────────────────────────────────────────── */
const LastUpdated = ({ date }) => (
  <div className="lp-last-updated">
    <span>Last updated: {date}</span>
  </div>
);

/* ── Dynamic content renderer ──────────────────────────────────────────────── */
const DynamicContent = ({ html, fallback }) => {
  if (html && html.trim()) {
    return (
      <div
        className="lp-dynamic-content"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return fallback;
};

/* ── Skeleton loader ───────────────────────────────────────────────────────── */
const ContentSkeleton = () => (
  <div className="lp-skeleton">
    {[80, 60, 95, 70, 85, 55].map((w, i) => (
      <div key={i} className="lp-sk-line" style={{ width: `${w}%` }} />
    ))}
  </div>
);

/* ── Full page layout wrapper ──────────────────────────────────────────────── */
const PolicyPageLayout = ({
  seoTitle,
  eyebrow,
  titlePlain,
  titleHighlight,
  intro,
  heroImage,
  heroKey,
  lastUpdated,
  loading,
  children,
  contact,
}) => (
  <>
    <Helmet>
      <title>{seoTitle} | Angel Printers</title>
    </Helmet>

    <div className="lp-page">
      {/* Hero */}
      <PolicyHero
        eyebrow={eyebrow}
        titlePlain={titlePlain}
        titleHighlight={titleHighlight}
        intro={intro}
        heroImage={heroImage}
        heroKey={heroKey}
      />

      {/* Divider */}
      <div className="lp-divider" />

      {/* Content */}
      <section className="lp-content-section">
        <div className="lp-content-wrap">
          {loading ? <ContentSkeleton /> : children}

          {/* Contact row at bottom */}
          {!loading && (
            <div className="lp-contact-section">
              <ContactRow contact={contact || DEFAULT_CONTACT} />
            </div>
          )}

          {/* Last Updated */}
          {!loading && <LastUpdated date={lastUpdated} />}
        </div>
      </section>
    </div>
  </>
);

/* ════════════════════════════════════════════════════════════════════════════
   STATIC FALLBACK CONTENT
   ════════════════════════════════════════════════════════════════════════════ */

const TermsFallback = () => (
  <>
    <Section number="1" title="Introduction">
      <P>These Terms and Conditions govern your use of the Angel Printers website ("Website") and the services provided by Angel Printers ("We", "Us" or "Our"). By accessing, browsing or using our Website, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, please do not use our Website or services.</P>
    </Section>
    <Section number="2" title="Use of Our Website">
      <P>You agree to use our Website only for lawful purposes and in accordance with these Terms and Conditions. You must not use our Website in any way that may damage, disable, overburden, or impair our servers or networks, or interfere with any other party's use and enjoyment of our Website.</P>
    </Section>
    <Section number="3" title="Products and Services">
      <P>We provide a range of printing products and custom printing services as listed on our Website. We make every effort to ensure that product descriptions, images and prices are accurate. However, we do not guarantee that the information is error-free, complete or up to date. We reserve the right to modify or discontinue any product or service without prior notice.</P>
    </Section>
    <Section number="4" title="Orders and Payments">
      <P>All orders placed through our Website are subject to acceptance and availability. Once your order is confirmed, you will receive an order confirmation via email or SMS. Payment for orders can be made through the available payment methods on our Website. We reserve the right to cancel or refuse any order at our discretion, including in cases of suspected fraud or payment issues.</P>
    </Section>
    <Section number="5" title="Delivery and Shipping">
      <P>We aim to deliver your order within the estimated time mentioned on the product page or in the order confirmation. Delivery times may vary depending on your location, product type and other factors. We are not responsible for delays caused by circumstances beyond our control, such as natural disasters, strikes or courier disruptions.</P>
    </Section>
    <Section number="6" title="Returns and Refunds">
      <P>Since most of our products are customized, returns and refunds are only applicable in cases of damage, defect or incorrect items received. Please refer to our Return &amp; Refund Policy for detailed information.</P>
    </Section>
    <Section number="7" title="Intellectual Property">
      <P>All content on this Website, including text, graphics, logos, images, and software, is the property of Angel Printers and is protected by copyright and other intellectual property laws. You may not use, reproduce or distribute any content from our Website without our prior written permission.</P>
    </Section>
    <Section number="8" title="Limitation of Liability">
      <P>We are not liable for any direct, indirect, incidental, special or consequential damages arising from your use of our Website or services, including but not limited to loss of profits, data or business opportunities.</P>
    </Section>
    <Section number="9" title="Changes to Terms">
      <P>We may update these Terms and Conditions from time to time. Any changes will be posted on this page with the updated effective date. Your continued use of the Website after changes are made constitutes your acceptance of the new Terms and Conditions.</P>
    </Section>
    <Section number="10" title="Contact Us">
      <P>If you have any questions about these Terms and Conditions, please contact us at:</P>
    </Section>
  </>
);

const PrivacyFallback = () => (
  <>
    <Section number="1" title="Information We Collect">
      <P>We collect personal information that you provide to us directly, such as your name, email address, phone number, and shipping address. We may also collect information about your device, browser, IP address, and how you interact with our website. This may include data about the pages you visit, the links you click, and the time you spend on our site.</P>
    </Section>
    <Section number="2" title="How We Use Your Information">
      <P>We use the information we collect to:</P>
      <Bullets items={[
        'Process and fulfill your orders',
        'Provide customer support',
        'Send important updates about your orders',
        'Improve our website, products, and services',
        'Prevent fraud and ensure the security of our platform',
      ]} />
    </Section>
    <Section number="3" title="Sharing Your Information">
      <P>We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who help us operate our website, process payments, deliver orders, or provide customer support. These partners are required to keep your information confidential and only use it for the purposes we specify.</P>
    </Section>
    <Section number="4" title="Cookies and Tracking Technologies">
      <P>We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content and ads. You can control cookies through your browser settings, but disabling them may affect your experience on our website.</P>
    </Section>
    <Section number="5" title="Your Rights">
      <P>You have the right to access, update, or delete your personal information. You can also opt out of receiving promotional communications from us at any time. To exercise these rights, please contact us using the details provided below.</P>
    </Section>
    <Section number="6" title="Data Security">
      <P>We take reasonable measures to protect your personal information from unauthorized access, misuse, or disclosure. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</P>
    </Section>
    <Section number="7" title="Changes to This Policy">
      <P>We may update this Privacy Policy from time to time. Any changes will be posted on this page with the updated date. We encourage you to review this policy periodically.</P>
    </Section>
    <Section number="8" title="Contact Us">
      <P>If you have any questions about this Privacy Policy, please contact us at:</P>
    </Section>
  </>
);

const ShippingFallback = () => (
  <>
    <Section number="1" title="Shipping Locations">
      <P>We currently deliver orders across India. If your location is not serviceable, please contact us before placing your order.</P>
    </Section>
    <Section number="2" title="Processing Time">
      <P>Orders are typically processed within 1–2 business days after payment confirmation. Custom or bulk orders may take longer. You will receive an email notification once your order has been shipped.</P>
    </Section>
    <Section number="3" title="Delivery Time">
      <P>Delivery time depends on your location and the shipping method selected at checkout. Generally, orders are delivered within 3–7 business days. For remote areas, it may take additional time. We will keep you updated with the tracking details once your order is dispatched.</P>
    </Section>
    <Section number="4" title="Shipping Charges">
      <P>Shipping charges are calculated based on the weight, size, and delivery location of your order. The exact shipping cost will be shown at checkout before you complete your purchase.</P>
    </Section>
    <Section number="5" title="Tracking Your Order">
      <P>Once your order is shipped, you will receive a tracking number via email or SMS. You can use this number to track your order in real time through our courier partner's website.</P>
    </Section>
    <Section number="6" title="Order Delays">
      <P>While we strive to deliver all orders on time, delays can occur due to unforeseen circumstances such as weather conditions, natural disasters, or courier issues. We will keep you informed in case of any delay.</P>
    </Section>
    <Section number="7" title="Damaged or Lost Packages">
      <P>If your package arrives damaged or is lost in transit, please contact us within 48 hours of delivery. We will work with the courier partner to resolve the issue and ensure you receive a replacement or refund as per our return policy.</P>
    </Section>
    <Section number="8" title="Contact Us">
      <P>If you have any questions about our Shipping Policy, please contact us at:</P>
    </Section>
  </>
);

/* ════════════════════════════════════════════════════════════════════════════
   PUBLIC PAGE EXPORTS
   ════════════════════════════════════════════════════════════════════════════ */

export const TermsPage = () => {
  const { data: legal, loading } = useSiteSettings('legal');
  const { data: contactData }    = useSiteSettings('contact');
  const policy  = legal?.termsOfService || {};
  const contact = contactData || DEFAULT_CONTACT;

  return (
    <PolicyPageLayout
      seoTitle="Terms & Conditions"
      // eyebrow="TERMS & CONDITIONS"
      titlePlain="Terms &"
      titleHighlight="Conditions"
      intro="Please read these Terms and Conditions carefully before using our website and services. By accessing or using our website, you agree to be bound by these terms and conditions."
      heroImage={policy.heroImage}
      heroKey="terms"
      lastUpdated={policy.lastUpdated || 'February 13, 2026'}
      loading={loading}
      contact={contact}
    >
      <DynamicContent html={policy.content} fallback={<TermsFallback />} />
    </PolicyPageLayout>
  );
};

export const PrivacyPage = () => {
  const { data: legal, loading } = useSiteSettings('legal');
  const { data: contactData }    = useSiteSettings('contact');
  const policy  = legal?.privacyPolicy || {};
  const contact = contactData || DEFAULT_CONTACT;

  return (
    <PolicyPageLayout
      seoTitle="Privacy Policy"
      // eyebrow="PRIVACY POLICY"
      titlePlain="Privacy"
      titleHighlight="Policy"
      intro="Your privacy matters to us. This Privacy Policy explains how we collect, use, and protect your personal information when you visit our website, use our services, or interact with us in any way."
      heroImage={policy.heroImage}
      heroKey="privacy"
      lastUpdated={policy.lastUpdated || 'February 13, 2026'}
      loading={loading}
      contact={contact}
    >
      <DynamicContent html={policy.content} fallback={<PrivacyFallback />} />
    </PolicyPageLayout>
  );
};

export const ShippingPage = () => {
  const { data: legal, loading } = useSiteSettings('legal');
  const { data: contactData }    = useSiteSettings('contact');
  const policy  = legal?.shippingPolicy || {};
  const contact = contactData || DEFAULT_CONTACT;

  return (
    <PolicyPageLayout
      seoTitle="Shipping Policy"
      // eyebrow="SHIPPING POLICY"
      titlePlain="Shipping"
      titleHighlight="Policy"
      intro="We aim to deliver your orders safely and on time. This Shipping Policy outlines our shipping methods, delivery times, charges, and the conditions that apply to your orders."
      heroImage={policy.heroImage}
      heroKey="shipping"
      lastUpdated={policy.lastUpdated || 'February 13, 2026'}
      loading={loading}
      contact={contact}
    >
      <DynamicContent html={policy.content} fallback={<ShippingFallback />} />
    </PolicyPageLayout>
  );
};

export default TermsPage;